// ---------- START IMPORT BLOCK ----------
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
// Services & Types
import UserService from '../Services/UserService';
import type { PatientDetailsResponse, PartialGeneralInfoUpdate, HealthBackgroundResponse } from '../../Types/schema-helpers';
// ---------- END IMPORT BLOCK ----------

interface PatientContextType {
  patientCache: Record<string, PatientDetailsResponse>;
  isLoadingPatient: boolean;
  error: string | null;
  fetchPatientByQuery: (query: string, forceRefresh?: boolean) => Promise<PatientDetailsResponse | null>;
  getPatientFromCache: (patientId: string) => PatientDetailsResponse | null;
  clearPatientCache: () => void;
  updatePatientProfile: (username: string, payload: PartialGeneralInfoUpdate) => Promise<boolean>;
  updateCachedHealthBackground: (usernameOrQuery: string, newHealthBg: HealthBackgroundResponse) => void;
}

const PatientContext = createContext<PatientContextType | null>(null);

export const usePatient = (): PatientContextType => {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error('usePatient must be used within a PatientProvider');
  }
  return context;
};

export const PatientProvider = ({ children }: { children: ReactNode }) => {
  const [patientCache, setPatientCache] = useState<Record<string, PatientDetailsResponse>>({});
  const [isLoadingPatient, setIsLoadingPatient] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Search and retrieve complete PatientDetailsResponse by query / ID.
   */
  const fetchPatientByQuery = useCallback(
    async (query: string, forceRefresh = false): Promise<PatientDetailsResponse | null> => {
      if (!query) return null;

      if (!forceRefresh && patientCache[query]) {
        return patientCache[query];
      }

      setIsLoadingPatient(true);
      setError(null);

      try {
        const response = await UserService.searchPatient(query);
        if (response) {
          setPatientCache((prev) => ({
            ...prev,
            [query]: response,
          }));
          return response;
        }
        return null;
      } catch (err: any) {
        const errorMessage = err?.message || `Failed to fetch patient data for query: ${query}`;
        console.error('Error fetching patient details:', err);
        setError(errorMessage);
        return null;
      } finally {
        setIsLoadingPatient(false);
      }
    },
    [patientCache]
  );

  /**
   * Updates partial patient general info and syncs state in local cache.
   */
  const updatePatientProfile = useCallback(
    async (username: string, payload: PartialGeneralInfoUpdate): Promise<boolean> => {
      if (!username) return false;

      setIsLoadingPatient(true);
      setError(null);

      try {
        const updatedFields = await UserService.updatePatientInfoByPro(username, payload);

        setPatientCache((prev) => {
          const nextCache = { ...prev };

          Object.keys(nextCache).forEach((key) => {
            const cachedPatient = nextCache[key];
            if (cachedPatient?.patient?.username === username) {
              nextCache[key] = {
                ...cachedPatient,
                patient: {
                  ...cachedPatient.patient,
                  ...updatedFields,
                },
              };
            }
          });

          return nextCache;
        });

        return true;
      } catch (err: any) {
        const errorMessage = err?.response?.data?.detail || err?.message || `Failed to update patient profile.`;
        console.error('Error updating patient profile:', err);
        setError(errorMessage);
        return false;
      } finally {
        setIsLoadingPatient(false);
      }
    },
    []
  );

  /**
   * 🎯 Instantly updates health_background in all cache keys matching the patient.
   */
  /**
   * Instantly updates health_background in all cache keys matching the patient.
   */
  const updateCachedHealthBackground = useCallback(
    (usernameOrQuery: string, newHealthBg: HealthBackgroundResponse) => {
      setPatientCache((prev) => {
        const nextCache = { ...prev };

        Object.keys(nextCache).forEach((key) => {
          const cached = nextCache[key];
          if (
            cached?.patient && // 👈 Ensures required 'patient' property remains preserved
            (key === usernameOrQuery ||
              cached.patient.username === usernameOrQuery ||
              (usernameOrQuery === 'me' && cached))
          ) {
            nextCache[key] = {
              ...cached,
              patient: cached.patient,
              health_background: newHealthBg,
            };
          }
        });

        return nextCache;
      });
    },
    []
  );

  /**
   * Synchronously access cached patient data if present
   */
  const getPatientFromCache = useCallback(
    (patientId: string): PatientDetailsResponse | null => {
      return patientCache[patientId] || null;
    },
    [patientCache]
  );

  const clearPatientCache = useCallback(() => {
    setPatientCache({});
    setError(null);
  }, []);

  const value = useMemo(
    () => ({
      patientCache,
      isLoadingPatient,
      error,
      fetchPatientByQuery,
      getPatientFromCache,
      clearPatientCache,
      updatePatientProfile,
      updateCachedHealthBackground,
    }),
    [
      patientCache,
      isLoadingPatient,
      error,
      fetchPatientByQuery,
      getPatientFromCache,
      clearPatientCache,
      updatePatientProfile,
      updateCachedHealthBackground,
    ]
  );

  return <PatientContext.Provider value={value}>{children}</PatientContext.Provider>;
};